import React, { useCallback, useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import {
  adminGetChatMessages,
  adminGetOverride,
  adminGetUserByWhatsapp,
  adminOpenChat,
  adminSendChatMessage,
  adminSendReopenTemplate,
  adminSetOverride,
} from "./adminApi";
import "./AdminDashboard.css";

const CHATBOT_NUMBER_OPTIONS = [
  { label: "Production (+6588707166)", value: "+6588707166" },
  { label: "Admin (+6598227828)", value: "+6598227828" },
  { label: "Sandbox (+16079009037)", value: "+16079009037" },
];

const DEFAULT_CHATBOT_NUMBER = "+6588707166";
const REOPEN_WINDOW_OK_MESSAGE = "[User clicked OK to reopen 24-hour window]";

const MESSAGE_TEMPLATES = [
  {
    id: "override-error",
    label: "Override Error Notice",
    text:
      "Hi, I'm the developer of this chatbot. I temporarily override the chatbot as there was an error message. For the essay grading, please send it as a pdf or photo of the essay, as we cannot accept the essay sent in the text message (too many characters). Thank you!",
  },
  {
    id: "credits-restored",
    label: "Credits Restored",
    text:
      "Thank you for trying out the grader! I do hope it helps! I'll add back some credit also due to the error. I'll disable the override now so it will go back to the grader again.",
  },
  {
    id: "review-request",
    label: "Review Request",
    text:
      "Hi, I'm the developer of this chatbot. I saw that your credit was running low so I wanted to ask if you found the grader useful, would you be able to help write a review for it? I'll add additional credit for you by way of thanks :) This is the Yotpo review link: https://yotpo.com/go/agWDSR53 (Thank you again! I'll disable the override now so the chat will go back to the grader again)",
  },
];

function getUserPayload(response) {
  return response?.data || response?.user || response || {};
}

function getOverridePayload(response) {
  return response?.data || response?.override || response || {};
}

function getMessagesPayload(response) {
  const data = response?.data || response || {};
  return data?.messages || response?.messages || [];
}

function getDefaultFromNumber(clientWhatsappNumber) {
  if (clientWhatsappNumber) {
    return clientWhatsappNumber;
  }

  const envBotNumber = process.env.REACT_APP_DEFAULT_ADMIN_WHATSAPP_NUMBER;
  return envBotNumber || DEFAULT_CHATBOT_NUMBER;
}

function getMessageSenderLabel(message) {
  const direction = String(message?.direction || "").toLowerCase();
  const senderType = String(message?.sender_type || "").toLowerCase();

  if (direction === "outbound" || senderType === "admin") {
    return "Admin";
  }

  return "User";
}

export default function AdminChatPage() {
  const location = useLocation();
  const navigate = useNavigate();
  const [whatsappNumber, setWhatsappNumber] = useState("");
  const [clientWhatsappNumber, setClientWhatsappNumber] = useState(DEFAULT_CHATBOT_NUMBER);
  const [currentWhatsappNumber, setCurrentWhatsappNumber] = useState("");
  const [currentClientWhatsappNumber, setCurrentClientWhatsappNumber] = useState(DEFAULT_CHATBOT_NUMBER);
  const [userData, setUserData] = useState(null);
  const [overrideData, setOverrideData] = useState(null);
  const [messages, setMessages] = useState([]);
  const [messageText, setMessageText] = useState("");
  const [overrideNote, setOverrideNote] = useState("");
  const [loading, setLoading] = useState(false);
  const [actionLoading, setActionLoading] = useState(false);
  const [sending, setSending] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");

  const loadChat = useCallback(async (targetWhatsapp, targetClientWhatsapp) => {
    if (!targetWhatsapp.trim()) {
      setError("Please enter a WhatsApp number.");
      return;
    }

    setLoading(true);
    setError("");
    setSuccess("");

    try {
      const openResponse = await adminOpenChat(
        targetWhatsapp.trim(),
        targetClientWhatsapp.trim()
      );

      setUserData(getUserPayload(openResponse?.user || null));
      setOverrideData(getOverridePayload(openResponse?.override || null));
      setMessages(openResponse?.messages || []);
      setCurrentWhatsappNumber(targetWhatsapp.trim());
      setCurrentClientWhatsappNumber(
        targetClientWhatsapp.trim() || DEFAULT_CHATBOT_NUMBER
      );
      setWhatsappNumber(targetWhatsapp.trim());
      setClientWhatsappNumber(
        targetClientWhatsapp.trim() || DEFAULT_CHATBOT_NUMBER
      );
    } catch (err) {
      if (err?.status === 401) {
        navigate("/admin/login", { replace: true });
        return;
      }
      setError(err.message || "Failed to open chat.");
    } finally {
      setLoading(false);
    }
  }, [navigate]);

  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const queryWhatsapp = params.get("whatsapp_number") || "";
    const queryClientWhatsapp = params.get("client_whatsapp_number") || "";

    if (queryWhatsapp) {
      loadChat(queryWhatsapp, queryClientWhatsapp);
    }
  }, [location.search, loadChat]);

  useEffect(() => {
    if (!currentWhatsappNumber) {
      return;
    }

    const intervalId = setInterval(async () => {
      try {
        const messagesResponse = await adminGetChatMessages(
          currentWhatsappNumber,
          100
        );
        setMessages(getMessagesPayload(messagesResponse));
      } catch (err) {
        if (err?.status === 401) {
          navigate("/admin/login", { replace: true });
          return;
        }

        console.error("Admin chat polling failed:", err);
      }
    }, 5000);

    return () => {
      clearInterval(intervalId);
    };
  }, [currentWhatsappNumber, navigate]);

  const refreshMessagesOnly = useCallback(async () => {
    if (!currentWhatsappNumber) {
      return;
    }

    const messagesResponse = await adminGetChatMessages(currentWhatsappNumber, 100);
    setMessages(getMessagesPayload(messagesResponse));
  }, [currentWhatsappNumber]);

  async function handleOpenChat(event) {
    event.preventDefault();
    await loadChat(whatsappNumber, clientWhatsappNumber);
  }

  async function handleRefresh() {
    if (!currentWhatsappNumber) {
      setError("Open a chat first.");
      return;
    }

    await loadChat(currentWhatsappNumber, currentClientWhatsappNumber);
  }

  async function handleOverrideChange(isActive) {
    if (!currentWhatsappNumber) {
      setError("Open a chat first.");
      return;
    }

    setActionLoading(true);
    setError("");
    setSuccess("");

    try {
      await adminSetOverride(currentWhatsappNumber, isActive, overrideNote);
      const overrideResponse = await adminGetOverride(currentWhatsappNumber);
      setOverrideData(getOverridePayload(overrideResponse));
      setSuccess(isActive ? "Override enabled." : "Override disabled.");
    } catch (err) {
      if (err?.status === 401) {
        navigate("/admin/login", { replace: true });
        return;
      }
      setError(err.message || "Failed to update override.");
    } finally {
      setActionLoading(false);
    }
  }

  async function handleSendReopenTemplate() {
    if (!currentWhatsappNumber) {
      setError("Open a chat before sending the template.");
      return;
    }

    const fromNumber = getDefaultFromNumber(
      currentClientWhatsappNumber || clientWhatsappNumber
    );

    if (!fromNumber) {
      setError("Please select a chatbot number first.");
      return;
    }

    setActionLoading(true);
    setError("");
    setSuccess("");

    try {
      await adminSendReopenTemplate(fromNumber, currentWhatsappNumber);
      setSuccess("24-hour window reopen template sent.");
      await refreshMessagesOnly();
    } catch (err) {
      if (err?.status === 401) {
        navigate("/admin/login", { replace: true });
        return;
      }
      setError(err.message || "Failed to send reopen template.");
    } finally {
      setActionLoading(false);
    }
  }

  async function handleSendMessage() {
    if (!currentWhatsappNumber) {
      setError("Open a chat first.");
      return;
    }

    if (!messageText.trim()) {
      setError("Please enter a message.");
      return;
    }

    const fromWhatsappNumber = getDefaultFromNumber(currentClientWhatsappNumber);
    if (!fromWhatsappNumber) {
      setError("Please enter the bot number in Client WhatsApp Number before sending.");
      return;
    }

    setSending(true);
    setError("");
    setSuccess("");

    try {
      await adminSendChatMessage(
        fromWhatsappNumber,
        currentWhatsappNumber,
        messageText.trim()
      );
      setMessageText("");
      const messagesResponse = await adminGetChatMessages(currentWhatsappNumber, 100);
      setMessages(getMessagesPayload(messagesResponse));
      setSuccess("Message sent.");
    } catch (err) {
      if (err?.status === 401) {
        navigate("/admin/login", { replace: true });
        return;
      }
      setError(err.message || "Failed to send message.");
    } finally {
      setSending(false);
    }
  }

  async function handleCopyTemplate(templateText) {
    try {
      await navigator.clipboard.writeText(templateText);
      setSuccess("Template copied.");
      setError("");
    } catch (err) {
      setError("Failed to copy template.");
      setSuccess("");
    }
  }

  return (
    <div className="admin-content">
      <div className="admin-card">
        <h2 className="admin-section-title">Admin Chat</h2>
        <form onSubmit={handleOpenChat}>
          <div className="admin-form-grid">
            <div className="admin-form-row">
              <label>WhatsApp Number</label>
              <input
                className="admin-input"
                value={whatsappNumber}
                onChange={(e) => setWhatsappNumber(e.target.value)}
                placeholder="+6598xxxxxx or whatsapp:+6598xxxxxx"
              />
            </div>

            <div className="admin-form-row">
              <label>Chatbot Number</label>
              <select
                className="admin-input"
                value={clientWhatsappNumber}
                onChange={(e) => setClientWhatsappNumber(e.target.value)}
              >
                {CHATBOT_NUMBER_OPTIONS.map((option) => (
                  <option key={option.value} value={option.value}>
                    {option.label}
                  </option>
                ))}
              </select>
            </div>
          </div>

          <div className="admin-button-row">
            <button
              type="submit"
              className="admin-button admin-button-primary"
              disabled={loading}
            >
              {loading ? "Opening..." : "Open Chat"}
            </button>
            <button
              type="button"
              className="admin-button admin-button-secondary"
              onClick={handleRefresh}
              disabled={loading || !currentWhatsappNumber}
            >
              Refresh
            </button>
          </div>
        </form>

        {error && <div className="admin-error" style={{ marginTop: 12 }}>{error}</div>}
        {success && <div className="admin-success" style={{ marginTop: 12 }}>{success}</div>}
      </div>

      <div className="admin-card admin-chat-panel">
        <div>
          <h3 className="admin-section-title">Chat Summary</h3>
          <div className="admin-summary-grid">
            <div className="admin-summary-item"><b>User WhatsApp:</b> {currentWhatsappNumber || "-"}</div>
            <div className="admin-summary-item"><b>User Email:</b> {userData?.user_email || "-"}</div>
            <div className="admin-summary-item"><b>User Status:</b> {userData?.user_status || "-"}</div>
            <div className="admin-summary-item"><b>Override Status:</b> {String(overrideData?.is_active ?? overrideData?.override_active ?? false)}</div>
            <div className="admin-summary-item"><b>reminder_science_grader_flag:</b> {userData?.reminder_science_grader_flag || "-"}</div>
            <div className="admin-summary-item"><b>reminder_generic_flag:</b> {userData?.reminder_generic_flag || "-"}</div>
          </div>
        </div>

        <div className="admin-form-row">
          <label>Override Note</label>
          <input
            className="admin-input"
            value={overrideNote}
            onChange={(e) => setOverrideNote(e.target.value)}
            placeholder="Optional note"
          />
        </div>

        <div className="admin-button-row">
          <button
            type="button"
            className="admin-button admin-button-primary"
            onClick={() => handleOverrideChange(true)}
            disabled={actionLoading || !currentWhatsappNumber}
          >
            Enable Override
          </button>
          <button
            type="button"
            className="admin-button admin-button-danger"
            onClick={() => handleOverrideChange(false)}
            disabled={actionLoading || !currentWhatsappNumber}
          >
            Disable Override
          </button>
          <button
            type="button"
            className="admin-button admin-button-secondary"
            onClick={handleSendReopenTemplate}
            disabled={actionLoading || !currentWhatsappNumber}
          >
            Send 24hr Reopen Template
          </button>
        </div>

        <div className="admin-meta">
          Use this if the 24-hour WhatsApp customer service window has expired.
        </div>

        <div className="admin-chat-messages">
          {messages.length === 0 ? (
            <div className="admin-meta">No messages yet.</div>
          ) : (
            messages.map((message, index) => {
              const senderLabel = getMessageSenderLabel(message);
              const isOutbound = senderLabel === "Admin";
              const isReopenWindowEvent =
                message?.message_text === REOPEN_WINDOW_OK_MESSAGE;

              return (
                <div
                  key={message?.id || `${message?.created_at || "msg"}_${index}`}
                  className={`admin-message-row ${
                    isOutbound ? "admin-message-row-outbound" : ""
                  }`}
                >
                  <div
                    className={`admin-message ${
                      isOutbound ? "admin-message-outbound" : "admin-message-inbound"
                    }`}
                    style={
                      isReopenWindowEvent
                        ? {
                            background: "#f8fafc",
                            borderStyle: "dashed",
                          }
                        : undefined
                    }
                  >
                    <div className="admin-message-header">
                      {senderLabel} · {message?.created_at || "-"}
                    </div>
                    <div className="admin-message-body">
                      {isReopenWindowEvent ? (
                        <span style={{ color: "#6b7280", fontStyle: "italic" }}>
                          System event: User clicked OK to reopen 24-hour window
                        </span>
                      ) : (
                        message?.message_text || "(no text)"
                      )}
                    </div>
                    {message?.context_text && (
                      <div className="admin-meta" style={{ marginTop: 8 }}>
                        Context: {message.context_text}
                      </div>
                    )}
                    {message?.media_url && (
                      <div style={{ marginTop: 8 }}>
                        <a
                          href={message.media_url}
                          target="_blank"
                          rel="noopener noreferrer"
                        >
                          View media
                        </a>
                      </div>
                    )}
                  </div>
                </div>
              );
            })
          )}
        </div>

        <div className="admin-form-row">
          <label>Template Messages</label>
          <div className="admin-template-list">
            {MESSAGE_TEMPLATES.map((template) => (
              <div key={template.id} className="admin-template-card">
                <div className="admin-template-title">{template.label}</div>
                <div className="admin-template-body">{template.text}</div>
                <div className="admin-button-row">
                  <button
                    type="button"
                    className="admin-button admin-button-secondary"
                    onClick={() => setMessageText(template.text)}
                  >
                    Use Template
                  </button>
                  <button
                    type="button"
                    className="admin-button admin-button-secondary"
                    onClick={() => handleCopyTemplate(template.text)}
                  >
                    Copy
                  </button>
                </div>
              </div>
            ))}
          </div>
        </div>

        <div className="admin-form-row">
          <label>Reply</label>
          <textarea
            className="admin-textarea"
            value={messageText}
            onChange={(e) => setMessageText(e.target.value)}
            placeholder="Type a manual support message"
          />
        </div>

        <div className="admin-button-row">
          <button
            type="button"
            className="admin-button admin-button-primary"
            onClick={handleSendMessage}
            disabled={sending}
          >
            {sending ? "Sending..." : "Send"}
          </button>
        </div>
      </div>
    </div>
  );
}
